import type { WebImageAttachment } from '../../services/web-email-submission/web-email-submission.shared'
import type { UploadPolicy } from '../contexts/policy'

const fileNameSafeCharacters = /[^a-zA-Z0-9._-]/g
const defaultFileNameLength = 120

export const sanitizeFileName = (fileName: string, maxFileNameLength = defaultFileNameLength) => {
  const rawName = fileName.split('/').pop()?.split('\\').pop() ?? ''
  return rawName.replace(fileNameSafeCharacters, '_').replace(/^\.+/, '').slice(0, maxFileNameLength)
}

const getFileExtension = (fileName: string) => {
  const extension = fileName.split('.').pop()
  return extension ? extension.toLowerCase() : ''
}

const fileToBase64 = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader()

    reader.onload = () => {
      const result = typeof reader.result === 'string' ? reader.result : ''
      const base64 = result.includes(',') ? result.split(',')[1] : ''
      if (!base64) {
        reject(new Error('Failed to encode one of the selected files.'))
        return
      }
      resolve(base64)
    }

    reader.onerror = () => reject(new Error('Could not read a selected file.'))
    reader.readAsDataURL(file)
  })

export const validateSelectedFiles = (files: File[], policy: UploadPolicy | null) => {
  if (policy && files.length > policy.maxAttachments) {
    return `You can upload up to ${policy.maxAttachments} images.`
  }

  let totalBytes = 0

  for (const file of files) {
    totalBytes += file.size

    if (!file.type.startsWith('image/')) {
      return `"${file.name}" is not an image.`
    }

    if (!policy) continue

    if (file.size <= 0 || file.size > policy.maxAttachmentSizeBytes) {
      return `"${file.name}" exceeds the per-file size limit.`
    }

    if (!policy.allowedImageMimeTypes.includes(file.type)) {
      return `"${file.name}" is not a supported image type.`
    }

    const safeName = sanitizeFileName(file.name, policy.maxFileNameLength)
    const extension = getFileExtension(safeName)
    const allowedExtensions = policy.mimeExtensionMap[file.type] ?? []

    if (!safeName || !extension || !allowedExtensions.includes(extension)) {
      return `"${file.name}" has an extension that does not match its image type.`
    }
  }

  if (policy && totalBytes > policy.maxTotalAttachmentSizeBytes) {
    return 'Combined image size is too large.'
  }

  return null
}

export const buildAttachmentPayload = async (files: File[], policy: UploadPolicy | null): Promise<WebImageAttachment[]> => {
  const maxFileNameLength = policy?.maxFileNameLength ?? defaultFileNameLength

  return Promise.all(
    files.map(async (file) => ({
      fileName: sanitizeFileName(file.name, maxFileNameLength),
      contentType: file.type,
      size: file.size,
      data: await fileToBase64(file)
    }))
  )
}
